import { useState } from 'react'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Button } from 'components/ui/Button/Button'
import { NoData } from 'components/NoData'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { Flex } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import { formatNumbersForDisplay } from 'utils/helpers'
import { IFuelContractorRechargeRow, IFuelMonthEndTankRow } from 'types/fuelCustody.interface'
import {
    FUEL_CONTRACTOR_RECHARGE_ROWS,
    FUEL_DIESEL_PRICE_GHS,
    FUEL_MONTH_END,
    FUEL_MONTH_END_TANK_ROWS,
} from 'pages/FuelManagement/fuelTanksDemoData'
import {
    LedgerLabel,
    LedgerOp,
    LedgerRow,
    LedgerValue,
    LedgerWrap,
    SignedText,
    SignOffWrap,
} from './FuelTanks.styled'
import {
    ContinuityNote,
    MemoHeadline,
    MemoNote,
    MemoRow,
    RechargeAmount,
    StatementCellText,
} from './MonthEndPanel.styled'

const fmt = (value: number): string => formatNumbersForDisplay(value)

const signedL = (value: number): string =>
    value === 0 ? '0 L' : `${value > 0 ? '+' : '−'}${fmt(Math.abs(value))} L`

export const MonthEndPanel = () => {
    const [preparedBy, setPreparedBy] = useState<string | null>(null)
    const close = FUEL_MONTH_END

    const tankColumns: IHonestTableColumn<IFuelMonthEndTankRow>[] = [
        {
            id: 'tank',
            header: 'Tank',
            sticky: true,
            minWidth: '9rem',
            cell: (item) => <StatementCellText $em>{item.tank}</StatementCellText>,
        },
        {
            id: 'opening',
            header: `Opening ${close.openingLabel}`,
            align: 'right',
            cell: (item) => <StatementCellText>{`${fmt(item.openingL)} L`}</StatementCellText>,
        },
        {
            id: 'purchases',
            header: 'Purchases',
            align: 'right',
            cell: (item) => <StatementCellText>{`${fmt(item.purchasesL)} L`}</StatementCellText>,
        },
        {
            id: 'issues',
            header: 'Issues incl. losses',
            align: 'right',
            cell: (item) => <StatementCellText>{`${fmt(item.issuesL)} L`}</StatementCellText>,
        },
        {
            id: 'transfers',
            header: 'Transfers',
            align: 'right',
            cell: (item) => <StatementCellText>{signedL(item.transfersL)}</StatementCellText>,
        },
        {
            id: 'closing',
            header: `Closing ${close.closingLabel}`,
            align: 'right',
            cell: (item) => <StatementCellText $em>{`${fmt(item.closingL)} L`}</StatementCellText>,
        },
    ]

    const rechargeColumns: IHonestTableColumn<IFuelContractorRechargeRow>[] = [
        {
            id: 'contractor',
            header: 'Contractor',
            sticky: true,
            minWidth: '11rem',
            cell: (item) => <StatementCellText $em>{item.contractor}</StatementCellText>,
        },
        {
            id: 'litres',
            header: 'Fuel drawn',
            align: 'right',
            cell: (item) => <StatementCellText>{`${fmt(item.litres)} L`}</StatementCellText>,
        },
        {
            id: 'amount',
            header: 'Recharge',
            align: 'right',
            cell: (item) => <RechargeAmount>{`GH₵ ${fmt(item.amountGhs)}`}</RechargeAmount>,
        },
        {
            id: 'status',
            header: 'Status',
            cell: (item) => <StatementCellText>{item.status}</StatementCellText>,
        },
    ]

    if (!USE_OPS_MOCKS) {
        return <NoData text='The month-end close pack generates from daily reconciliations once fuel data is connected.' />
    }

    const handlePrepare = () => {
        setPreparedBy(`Milos Mladenovic · ${format(new Date(), 'HH:mm')}`)
        toast.success('Close pack prepared — awaiting GM approval')
    }

    return (
        <Flex column gap='1rem' width='100%'>
            <Container>
                <ContainerHeader>
                    <Flex between center width='100%'>
                        <Flex center gap='0.75rem'>
                            <TitleText>{`Site statement — ${close.monthLabel} close`}</TitleText>
                            <DemoBadge />
                        </Flex>
                        <Button variant='black' size='sm' onClick={() => window.print()}>Print / PDF</Button>
                    </Flex>
                </ContainerHeader>
                <ContainerContent gap='0.75rem'>
                    <LedgerWrap>
                        <LedgerRow>
                            <LedgerOp />
                            <LedgerLabel>{`Opening stock ${close.openingLabel}`}</LedgerLabel>
                            <LedgerValue>{`${fmt(close.openingL)} L`}</LedgerValue>
                        </LedgerRow>
                        <LedgerRow>
                            <LedgerOp>+</LedgerOp>
                            <LedgerLabel>Purchases</LedgerLabel>
                            <LedgerValue>{`${fmt(close.purchasesL)} L`}</LedgerValue>
                        </LedgerRow>
                        <LedgerRow>
                            <LedgerOp>−</LedgerOp>
                            <LedgerLabel>Issues</LedgerLabel>
                            <LedgerValue>{`${fmt(close.issuesL)} L`}</LedgerValue>
                        </LedgerRow>
                        <LedgerRow>
                            <LedgerOp>±</LedgerOp>
                            <LedgerLabel>Transfers net</LedgerLabel>
                            <LedgerValue>{`${fmt(close.transfersNetL)} L`}</LedgerValue>
                        </LedgerRow>
                        <LedgerRow>
                            <LedgerOp>−</LedgerOp>
                            <LedgerLabel>Losses — closed cases</LedgerLabel>
                            <LedgerValue>{`${fmt(close.lossesClosedL)} L`}</LedgerValue>
                        </LedgerRow>
                        <LedgerRow $em>
                            <LedgerOp>=</LedgerOp>
                            <LedgerLabel>{`Closing book stock ${close.closingLabel}`}</LedgerLabel>
                            <LedgerValue $em>{`${fmt(close.closingL)} L`}</LedgerValue>
                        </LedgerRow>
                        <MemoRow>
                            <MemoHeadline>
                                {`Pending write-off — ${fmt(close.pendingWriteOffL)} L (GH₵ ${fmt(close.pendingWriteOffGhs)}) · awaiting GM approval`}
                            </MemoHeadline>
                            <MemoNote>{close.memoNote}</MemoNote>
                        </MemoRow>
                    </LedgerWrap>
                    <ContinuityNote>{close.continuityNote}</ContinuityNote>
                </ContainerContent>
            </Container>

            <Container>
                <ContainerHeader>
                    <TitleText>{`Per-tank statements — ${close.monthLabel}`}</TitleText>
                </ContainerHeader>
                <ContainerContent gap='0.75rem'>
                    <HonestTable
                        columns={tankColumns}
                        data={FUEL_MONTH_END_TANK_ROWS}
                        keyField='id'
                        emptyMessage='No tank statements yet.'
                    />
                    <OtherText grey>
                        {`Issues column includes the ${fmt(close.lossesClosedL)} L of closed losses · closings sum to ${fmt(close.closingL)} L.`}
                    </OtherText>
                </ContainerContent>
            </Container>

            <Container>
                <ContainerHeader>
                    <TitleText>{`Contractor fuel — ${close.monthLabel.split(' ')[0]}`}</TitleText>
                </ContainerHeader>
                <ContainerContent gap='0.75rem'>
                    <HonestTable
                        columns={rechargeColumns}
                        data={FUEL_CONTRACTOR_RECHARGE_ROWS}
                        keyField='id'
                        emptyMessage='No contractor fuel drawn this month.'
                    />
                    <OtherText grey>
                        {`Recharged at GH₵ ${FUEL_DIESEL_PRICE_GHS.toFixed(2)}/L — line-level dispenses carry the contractor tag in the dispense log.`}
                    </OtherText>
                    <SignOffWrap>
                        {preparedBy ? (
                            <SignedText>{`✓ Prepared by ${preparedBy} · Approved by —`}</SignedText>
                        ) : (
                            <>
                                <OtherText grey>Prepared by — · Approved by —</OtherText>
                                <Button size='sm' variant='primary' onClick={handlePrepare}>Sign off</Button>
                            </>
                        )}
                    </SignOffWrap>
                </ContainerContent>
            </Container>
        </Flex>
    )
}
